import { signOut } from "@/api/auth";
import { getLearningsOf } from "@/api/learning";
import { getGroups } from "@/api/group";
import { getRoomReservations } from "@/api/room_reservations";
import { fetchRooms } from "@/api/room";
import { getStudent } from "@/api/student";
import { Learning } from "@/entities/learning";
import { Message } from "@/entities/message";
import { Group } from "@/entities/group";
import { Role } from "@/entities/role";
import { Room } from "@/entities/room";
import { getDocIdsOfStudent, Student } from "@/entities/student";
import { ComplementedSubmission } from "@/entities/submission";
import { ComplementedThread } from "@/entities/thread";
import router from "@/router";
import { createStore, Store } from "vuex";
import dayjs from "dayjs";
import "dayjs/locale/ja";
import { RoomRegularlyReservation } from "@/entities/room_regularly_reservation";

export type LearningPageType = "future" | "history" | "none";

type AppState = {
  loading: boolean;
  loadText: string;
  redirect: string;
  initLiff: boolean;
  isInLiff: boolean;
  role: Role | null;
  student: Student | null;
  classroomDocId: string;
  schoolDocId: string;
  submissions: ComplementedSubmission[];
  submissionsInitialized: boolean;
  submissionsError: string;
  threads: ComplementedThread[];
  threadsInitialized: boolean;
  threadsError: string;
  learnings: Learning[];
  learningLoading: boolean;
  visibleLearningsLength: number;
  learningFinished: boolean;
  learningPageType: LearningPageType;
  learning: Learning | null;
  groups: Group[];
  messageStream: (() => void) | null;
  messages: Message[];
  messageLoading: boolean;
  messageFinished: boolean;
  messageLimit: number;
  rooms: Room[];
  roomLoaded: boolean;
  roomReservations: RoomRegularlyReservation[];
  isAndroid: boolean;
};
const messageLimit = 20;
const lengthOfLearningsToRevealAtOnce = 20;

export default createStore({
  state: {
    loading: false,
    loadText: "",
    redirect: "/",
    initLiff: false,
    isInLiff: false,
    role: null,
    student: null,
    classroomDocId: "",
    schoolDocId: "",
    submissions: [],
    submissionsInitialized: false,
    submissionsError: "",
    threads: [],
    threadsInitialized: false,
    threadsError: "",
    learnings: [],
    learningLoading: true,
    learningFinished: false,
    visibleLearningsLength: 0,
    learningPageType: "none",
    learning: null,
    groups: [],
    messageStream: null,
    messages: [],
    messageLoading: false,
    messageFinished: false,
    messageLimit: messageLimit,
    rooms: [],
    roomLoaded: false,
    roomReservations: [],
    isAndroid: false
  } as AppState,
  mutations: {
    SET_LOADING(state, loading: boolean) {
      state.loading = loading;
    },
    SET_LOAD_TEXT(state, text: string) {
      state.loadText = text;
    },
    SET_REDIRECT(state, redirect: string) {
      state.redirect = redirect;
    },
    SET_INIT_LIFF(state, initLiff: boolean) {
      state.initLiff = initLiff;
    },
    SET_IS_IN_LIFF(state, isInLiff: boolean) {
      state.isInLiff = isInLiff;
    },
    SET_ROLE(state, role: Role) {
      state.role = role;
    },
    SET_STUDENT(state, student: Student) {
      state.student = student;
    },
    SET_SCHOOL_DOC_ID(state, schoolDocId: string) {
      state.schoolDocId = schoolDocId;
    },
    SET_CLASSROOM_DOC_ID(state, classroomDocId: string) {
      state.classroomDocId = classroomDocId;
    },
    SET_SUBMISSIONS(state, submissions: ComplementedSubmission[]) {
      state.submissions = submissions;
    },
    SET_SUBMISSIONS_INITIALIZED(state, initialized: boolean) {
      state.submissionsInitialized = initialized;
    },
    SET_SUBMISSIONS_ERROR(state, message: string) {
      state.submissionsError = message;
    },
    SET_THREADS(state, threads: ComplementedThread[]) {
      state.threads = threads;
    },
    SET_THREADS_INITIALIZED(state, initialized: boolean) {
      state.threadsInitialized = initialized;
    },
    SET_THREADS_ERROR(state, message: string) {
      state.threadsError = message;
    },
    SET_LEARNINGS(state, learnings: Learning[]) {
      state.learnings = learnings;
    },
    SET_LEARNING_LOADING(state, loading: boolean) {
      state.learningLoading = loading;
    },
    SET_LEARNING_FINISHED(state, finished: boolean) {
      state.learningFinished = finished;
    },
    SET_VISIBLE_LEARNINGS_LENGTH(state, length: number) {
      state.visibleLearningsLength = length;
    },
    SET_LEARNING_PAGE_TYPE(state, type: LearningPageType) {
      state.learningPageType = type;
    },
    SET_LEARNING(state, learning: Learning | null) {
      state.learning = learning;
    },
    SET_GROUPS(state, groups: Group[]) {
      state.groups = groups;
    },
    SET_ROOM_RESERVATIONS(state, roomReservations: RoomRegularlyReservation[]) {
      state.roomReservations = roomReservations;
    },
    SET_MESSAGE_STREAM(state, stream: (() => void) | null) {
      state.messageStream = stream;
    },
    SET_MESSAGES(state, messages: Message[]) {
      state.messages = messages;
    },
    SET_MESSAGE_LOADING(state, loading: boolean) {
      state.messageLoading = loading;
    },
    SET_MESSAGE_FINISHED(state, finished: boolean) {
      state.messageFinished = finished;
    },
    SET_ROOMS(state, rooms: Room[]) {
      state.rooms = rooms;
    },
    SET_ROOM_LOADED(state, loaded: boolean) {
      state.roomLoaded = loaded;
    },
    SET_IS_ANDROID(state, isAndroid: boolean) {
      state.isAndroid = isAndroid;
    },
    SIGN_OUT(state) {
      state.redirect = "/";
      state.isInLiff = false;
      state.role = null;
      state.student = null;
      state.classroomDocId = "";
      state.schoolDocId = "";
      state.learnings = [];
      state.learningLoading = false;
      state.learningFinished = false;
      state.learningPageType = "none";
      state.learning = null;
      state.messageStream = null;
      state.messages = [];
      state.messageLoading = false;
      state.messageFinished = false;
      state.rooms = [];
      state.roomLoaded = false;
    }
  },
  actions: {
    async getLearnings(context) {
      if (!context.state.student) {
        return;
      }

      const learnings = await getLearningsOf(context.state.student.ref);
      if (!learnings) {
        alert("学習履歴の取得に失敗しました");
        return;
      }
      if (learnings.length <= lengthOfLearningsToRevealAtOnce) {
        context.commit("SET_VISIBLE_LEARNINGS_LENGTH", learnings.length);
        context.commit("SET_LEARNING_FINISHED", true);
      } else {
        context.commit(
          "SET_VISIBLE_LEARNINGS_LENGTH",
          lengthOfLearningsToRevealAtOnce
        );
      }
      context.commit("SET_LEARNINGS", learnings);
      context.commit("SET_LEARNING_LOADING", false);
    },
    async getGroups(context) {
      if (!context.state.schoolDocId) {
        return;
      }
      const groups = await getGroups(context.state.schoolDocId);
      context.commit("SET_GROUPS", groups);
    },
    async getRoomReservations(context) {
      const reservations = await getRoomReservations();
      context.commit("SET_ROOM_RESERVATIONS", reservations);
    },
    async updateStudent(context) {
      if (!context.state.role) {
        return;
      }
      const student = await getStudent(context.state.role.data.ref);
      context.commit("SET_STUDENT", student);
    },
    async getMoreLearning(context) {
      if (
        context.state.learningFinished ||
        context.state.learningLoading ||
        !context.state.student ||
        context.state.learnings.length === 0
      ) {
        return;
      }

      if (
        context.state.learnings.length <=
        context.state.visibleLearningsLength + lengthOfLearningsToRevealAtOnce
      ) {
        context.commit(
          "SET_VISIBLE_LEARNINGS_LENGTH",
          context.state.learnings.length
        );
        context.commit("SET_LEARNING_FINISHED", true);
      } else {
        context.commit(
          "SET_VISIBLE_LEARNINGS_LENGTH",
          context.state.visibleLearningsLength + lengthOfLearningsToRevealAtOnce
        );
      }
    },
    async getRooms(context) {
      if (!context.state.student) {
        return;
      }

      try {
        const docIds = getDocIdsOfStudent(context.state.student);
        const rooms = await fetchRooms(
          docIds[2],
          docIds[0],
          context.state.student.data.target.id
        );
        context.commit("SET_ROOMS", rooms);
        context.commit("SET_ROOM_LOADED", true);
      } catch (e) {
        console.error(e);
        alert(e);
      }
    },
    async signOut() {
      await signOut();
      router.go(0);
    }
  },
  modules: {},
  getters: {
    involvedGroups: (state): Group[] => {
      return state.groups.filter(g =>
        g.data.students.some(ref => ref.id === state.student?.ref.id)
      );
    },
    involvedRoomReservations: (state): RoomRegularlyReservation[] => {
      const involvedGroups = state.groups.filter(g => {
        return g.data.students.some(ref => ref.id === state.student?.ref.id);
      });
      const _involved = state.roomReservations
        .filter(r =>
          involvedGroups.some(g => g.data.title === r.data.groupName)
        )
        .sort((a, b) => {
          const [_ahh, _amm] = a.data.time.split(":").map(_ => +_);
          const [_bhh, _bmm] = b.data.time.split(":").map(_ => +_);
          return dayjs()
            .hour(_ahh)
            .minute(_amm)
            .isBefore(
              dayjs()
                .hour(_bhh)
                .minute(_bmm)
            )
            ? -1
            : 1;
        });
      if (_involved.length === 0) return [];
      else if (_involved.length === 1) {
        return _involved;
      } else {
        // 一番開始時刻が近いコマの予約が先頭になるようにする
        const now = dayjs();
        let nextReservation: RoomRegularlyReservation | null = null;
        _involved.forEach(r => {
          if (nextReservation) return;
          const [hh, mm] = r.data.end.split(":").map(_ => +_);
          const end = dayjs()
            .hour(hh)
            .minute(mm);
          if (now.isBefore(end)) nextReservation = r;
        });
        if (!nextReservation) nextReservation = _involved[0];
        return [
          nextReservation,
          ..._involved.filter(r => r.ref.id !== nextReservation?.ref.id)
        ];
      }
    },
    consecutiveLearningDayCount: (state): number => {
      if (state.learnings.length === 0) return 0;
      let count = 0;
      let pointedDay = dayjs().startOf("day");
      for (
        let i = 0, l = state.learnings.length, isTodaysLearningCounted = false;
        i < l;
        i++
      ) {
        const d = dayjs.unix(state.learnings[i].data.timestamp).startOf("day");
        const diff = d.diff(pointedDay, "day");
        if (diff === 0) {
          // 当日に学習記録があれば count 加算。それ以外は次の比較用の dayjs オブジェクトのみ更新
          if (
            !isTodaysLearningCounted &&
            pointedDay.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
          ) {
            count += 1;
            isTodaysLearningCounted = true;
          }
          pointedDay = d;
        } else if (diff === -1) {
          // 比較対象と１日差であれば count 加算
          count += 1;
          pointedDay = d;
        } else {
          // １日以上間隔が空いているので検証終了
          break;
        }
      }
      return count;
    },
    maxConsecutiveLearningDayCount: (state): number => {
      if (state.learnings.length === 0) return 0;
      let max = 1;
      let count = 0;
      let pointedDay = dayjs().startOf("day");
      for (
        let i = 0, l = state.learnings.length, isTodaysLearningCounted = false;
        i < l;
        i++
      ) {
        const d = dayjs.unix(state.learnings[i].data.timestamp).startOf("day");
        const diff = d.diff(pointedDay, "day");
        console.log(diff, d.format("YYYY-MM-DD"));
        if (diff === 0) {
          // 当日に学習記録があれば 1 回だけ count 加算。それ以外は次の比較用の dayjs オブジェクトのみ更新
          if (
            !isTodaysLearningCounted &&
            pointedDay.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
          ) {
            count += 1;
            isTodaysLearningCounted = true;
          }
          // 最も古い学習ログに到達した場合ここで代入
          if (i === l - 1) max = count > max ? count : max;
        } else if (diff === -1) {
          // 比較対象と１日差であれば count 加算
          if (
            count === 0 &&
            pointedDay.format("YYYY-MM-DD") !== dayjs().format("YYYY-MM-DD")
          ) {
            // 当日に記録がない場合、最初の基準日はこの時点で数えられていないので加算
            count += 1;
          }
          count += 1;
          // 最も古い学習ログに到達した場合ここで代入
          if (i === l - 1) max = count > max ? count : max;
        } else {
          // １日以上間隔が空いているので、他の連続している区間との比較を行い終了
          max = count > max ? count : max;
          count = 0;
        }
        pointedDay = d;
      }
      return max;
    },
    totalLearningDayCount: (state): number => {
      if (state.learnings.length === 0) return 0;
      let count = 0;
      let pointedDay = dayjs().startOf("day");
      for (
        let i = 0, l = state.learnings.length, isTodaysLearningCounted = false;
        i < l;
        i++
      ) {
        const d = dayjs.unix(state.learnings[i].data.timestamp).startOf("day");
        const diff = d.diff(pointedDay, "day");
        if (diff === 0) {
          // 当日に学習記録があれば count 加算。それ以外は次の比較用の dayjs オブジェクトのみ更新
          if (
            !isTodaysLearningCounted &&
            pointedDay.format("YYYY-MM-DD") === dayjs().format("YYYY-MM-DD")
          ) {
            count += 1;
            isTodaysLearningCounted = true;
          }
          pointedDay = d;
        } else if (diff < 0) {
          // 比較対象と差があれば count 加算
          count += 1;
          pointedDay = d;
        }
      }
      return count;
    },
    habitChain: (state): { label: string; active: boolean }[] => {
      const data = [0, 1, 2, 3, 4, 5, 6].map(n => ({
        label: n === 0 ? "今日" : `${n} 日前`,
        active: false
      }));
      for (let i = 0; i < 7; i++) {
        const d = dayjs()
          .add(-1 * i, "day")
          .startOf("day");
        data[i].active = state.learnings.some(l => {
          return dayjs
            .unix(l.data.timestamp)
            .startOf("day")
            .isSame(d);
        });
      }
      return data;
    }
  }
});
export const awaitStudentLoaded = async (
  context: Store<AppState>
): Promise<Student> => {
  if (context.state.student) return context.state.student;
  await new Promise(resolve => setTimeout(resolve, 250));
  return await awaitStudentLoaded(context);
};
export const awaitLearningsLoaded = async (
  context: Store<AppState>
): Promise<Learning[]> => {
  if (!context.state.learningLoading) return context.state.learnings;
  await new Promise(resolve => setTimeout(resolve, 250));
  return await awaitLearningsLoaded(context);
};
export const awaitLiffInitialized = async (
  context: Store<AppState>
): Promise<boolean> => {
  if (context.state.initLiff) return context.state.initLiff;
  await new Promise(resolve => setTimeout(resolve, 250));
  return await awaitLiffInitialized(context);
};

export const isWaitingRoomOpen = (
  startTime: string,
  endTime: string,
  now?: dayjs.Dayjs
): boolean => {
  const _now = now ?? dayjs();
  const [h1, m1] = startTime.split(":").map((_: string) => +_);
  const [h2, m2] = endTime.split(":").map((_: string) => +_);
  const start = dayjs()
    .startOf("day")
    .hour(h1)
    .minute(m1);
  const end = dayjs()
    .startOf("day")
    .hour(h2)
    .minute(m2);
  return (
    (start.diff(_now, "minute") >= 0 && start.diff(_now, "minute") < 5) ||
    (start.isBefore(_now) && end.isAfter(_now))
  );
};
